import type { PageLoaderName } from './page-loaders';
import type { ShortMarketSource } from '@/client/pages/btc-5m/api';
import { normaliseEventId } from '@/client/pages/btc-5m/round-index';
import { shortRoundsFeedQueryOptions } from '@/client/pages/btc-5m/use-rounds-feed';
import { marketAddressFromParam } from '@/client/pages/market/market-address';
import { buildEventDetailQueryOptions } from '@/libs/react-sdk/api/use-event-detail';
import { marketDetailQueryOptions } from '@/libs/react-sdk/api/use-market-detail';
import type { ApiInstance } from '@/libs/sdk/api';
import type { FetchQueryOptions, QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * Route-aware warmup for the cold first paint (BON-254 #5). main.tsx runs it before the i18n await,
 * so the route chunk and the page's first queries travel in parallel with the locale files instead
 * of one round trip each after the first render. Everything here reuses the pages' own loaders
 * (PAGE_LOADERS) and query builders: a mounted page finds its entry in flight or settled under the
 * key it asks for and sends nothing twice. Nothing here needs a session; the user pages warm their
 * chunk only.
 */
// any: the options come from builders typed on their own data (EventDetail, MarketDetail,
// RoundFetch); the runner only hands them to prefetchQuery and never reads the data.
// prefetchQuery ignores `enabled`: a builder called with an empty id would still hit the BFF, so the
// route branches below skip the query when the id is empty.
export type BootQueryOptions = FetchQueryOptions<any, Error, any, QueryKey>;
export type BootPrefetchAction = { kind: 'chunk'; name: PageLoaderName } | { kind: 'query'; options: BootQueryOptions };
export type BootPrefetchContext = {
  api: ApiInstance;
  /** rounds.json source when the tenant has a valid VITE_SHORT_MARKETS_API_PATH (use-rounds-feed shortMarketSource). */
  shortMarketSource?: ShortMarketSource;
  /** /rewards is registered (confs/routes.tsx: enrollment on, or a distributor configured). */
  rewardsRoute: boolean;
};

// Paths as confs/routes.tsx declares them and wouter matches them: one segment per param, an
// optional trailing slash. The home page is in the entry bundle and warms nothing.
const EVENT_PATH = /^\/event\/([^/]+)$/;
const MARKET_PATH = /^\/market\/([^/]+)$/;
const USER_PATH = /^\/user\/([^/]+)(?:\/([^/]+))?$/;
const STATIC_CHUNKS = new Map<string, PageLoaderName[]>([
  ['/leaderboard', ['leaderboard']],
  ['/how-it-works', ['how-it-works']],
  ['/pdf', ['pdf']],
  // Auth redirects to /user/<uid>: the redirect chunk, then the page it lands on.
  ['/user', ['user-auth-redirect', 'user']],
  ['/profile', ['user-auth-redirect', 'user']],
]);
const USER_CHILD_CHUNKS = new Map<string, PageLoaderName>([
  ['settings', 'user-settings'],
  ['invite-earn', 'user-invite-earn'],
]);

const chunk = (name: PageLoaderName): BootPrefetchAction => ({ kind: 'chunk', name });
const query = (options: BootQueryOptions): BootPrefetchAction => ({ kind: 'query', options });

export const bootPrefetchFor = (
  pathname: string,
  { api, shortMarketSource, rewardsRoute }: BootPrefetchContext
): BootPrefetchAction[] => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/rewards') return rewardsRoute ? [chunk('activity-rewards-live')] : [];
  const fixed = STATIC_CHUNKS.get(path);
  if (fixed) return fixed.map(name => chunk(name));

  const event = EVENT_PATH.exec(path);
  if (event) {
    // Bare lower-case id: the only spelling the BFF accepts, and the one the event page and the
    // 5-minute page query (normaliseEventId). The rounds feed decides whether the id is a 5-minute
    // round (short-round-gate) before anything renders. Data warmups never retry: the mounted page
    // retries on its own (retryOnMount), a retried warmup only adds requests to a 5xx.
    const eventId = normaliseEventId(event[1]);
    const actions = [chunk('event')];
    if (eventId) actions.push(query({ ...buildEventDetailQueryOptions(api, { eventId }), retry: false }));
    if (shortMarketSource) actions.push(query(shortRoundsFeedQueryOptions(shortMarketSource)));
    return actions;
  }
  const market = MARKET_PATH.exec(path);
  if (market) {
    const marketAddress = marketAddressFromParam(market[1]);
    return marketAddress
      ? [chunk('market'), query({ ...marketDetailQueryOptions(api, { marketAddress }), retry: false })]
      : [chunk('market')];
  }
  const user = USER_PATH.exec(path);
  if (user) {
    const [, id, child] = user;
    // /user/notification is the not-found page with notifications off (routes.tsx), and so is an
    // unknown child segment: nothing to warm in either case.
    if (id === 'notification' && !child) return [];
    if (!child) return [chunk('user')];
    const childChunk = USER_CHILD_CHUNKS.get(child);
    return childChunk ? [chunk('user'), chunk(childChunk)] : [];
  }
  return [];
};

export type BootPrefetchDeps = {
  prefetchRoute: (name: PageLoaderName) => void;
  queryClient: Pick<QueryClient, 'prefetchQuery'>;
};

/** Fires the actions and forgets them: a warmup that fails must never block or break the first render. */
export const runBootPrefetch = (actions: BootPrefetchAction[], { prefetchRoute, queryClient }: BootPrefetchDeps) => {
  for (const action of actions) {
    try {
      if (action.kind === 'chunk') prefetchRoute(action.name);
      else void queryClient.prefetchQuery(action.options).catch(() => undefined);
    } catch {
      // prefetchRoute and prefetchQuery swallow their own rejections; this guards a synchronous throw.
    }
  }
};
